
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import AuthService from "./AuthService"
import { countryCodes } from "./CountryCodes"
import "./Signup.css"

const SignupScreen = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [phone, setPhone] = useState("")
  const [otp, setOtp] = useState("")
  const [selectedCountry, setSelectedCountry] = useState(countryCodes.find((c) => c.code === "+91") || countryCodes[0])
  const [otpSent, setOtpSent] = useState(false)
  const [confirmationResult, setConfirmationResult] = useState(null)
  const [timeoutSeconds, setTimeoutSeconds] = useState(60)
  const [timer, setTimer] = useState(null)
  const [notification, setNotification] = useState({ message: "", type: "" })

  useEffect(() => {
    return () => {
      if (timer) clearInterval(timer)
      AuthService.clearRecaptcha()
    }
  }, [timer])

  const showNotification = (message, type = "info") => {
    setNotification({ message, type })
    setTimeout(() => setNotification({ message: "", type: "" }), 5000)
  }

  const startTimer = () => {
    setTimeoutSeconds(60)
    const newTimer = setInterval(() => {
      setTimeoutSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(newTimer)
          resetToInitialState()
          showNotification("⏰ Process timeout. Please start over.", "warning")
          return 0
        }
        return prev - 1
      })
    }, 1000)
    setTimer(newTimer)
  }

  const resetToInitialState = () => {
    setOtpSent(false)
    setOtp("")
    setConfirmationResult(null)
    setIsLoading(false)
    if (timer) clearInterval(timer)
    AuthService.clearRecaptcha()
  }

  const handleSendOTP = async () => {
    if (isLoading) return

    if (!phone) {
      showNotification("Please enter phone number", "error")
      return
    }

    if (selectedCountry.code === "+91" && !AuthService.isValidPhone(phone)) {
      showNotification("Please enter a valid 10-digit phone number", "error")
      return
    }

    setIsLoading(true)
    const fullPhoneNumber = `${selectedCountry.code}${phone}`

    try {
      // Check if phone number already exists
      const phoneExists = await AuthService.checkPhoneInDatabase(fullPhoneNumber)
      if (phoneExists) {
        showNotification("❌ Phone number already registered. Please login instead.", "error")
        setTimeout(() => navigate("/login"), 2000)
        return
      }

      // Setup reCAPTCHA
      const recaptchaVerifier = AuthService.setupRecaptcha("recaptcha-container-signup")

      showNotification("📱 Sending OTP...", "info")

      const confirmationResult = await AuthService.sendOTP(fullPhoneNumber, recaptchaVerifier)

      setConfirmationResult(confirmationResult)
      setOtpSent(true)
      setIsLoading(false)
      startTimer()
      showNotification(`✅ OTP sent to ${fullPhoneNumber}`, "success")
    } catch (error) {
      console.error("❌ Error sending OTP:", error)
      showNotification(`❌ ${error}`, "error")
      setIsLoading(false)
    }
  }

 const handleVerifyOTP = async (enteredOtp) => {
  const otpToVerify = enteredOtp || otp  // ✅ use passed OTP or state value

  if (isLoading) return

  if (!otpToVerify || otpToVerify.length !== 6) {
    showNotification("Please enter 6-digit OTP", "error")
    return
  }

  setIsLoading(true)

  try {
    const result = await AuthService.verifyOTP(confirmationResult, otpToVerify)
    const user = result.user

    if (user) {
      if (timer) clearInterval(timer)
      AuthService.clearRecaptcha()

      const userExists = await AuthService.checkUserExists(user.uid)

      if (userExists) {
        showNotification("✅ Account already exists. Redirecting to dashboard...", "success")
        navigate("/dashboard")
      } else {
        showNotification("✅ Phone verified. Complete your profile.", "success")
        navigate("/profile-complete")
      }
    }
  } catch (error) {
    console.error("❌ OTP verification failed:", error)
    showNotification(`❌ ${error}`, "error")
  } finally {
    setIsLoading(false)
  }
}


 const handleKeyDown = (e, action) => {
    if (e.key === "Enter") {
      e.preventDefault()
      action()
    }
  }

  return (
    <div className="signup-screen">
      <div className="signup-container">
        <div className="signup-left">
          <div className="brand-section">
            <div className="brand-logo">
              <div className="logo-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <span className="brand-name">Altrange</span>
            </div>
            <h1 className="welcome-title">Join Altrange</h1>
            <p className="welcome-subtitle">Create your account and start your journey with our secure platform.</p>
          </div>

          <div className="signup-steps">
            <div className={`step-item ${!otpSent ? "active" : "completed"}`}>
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Phone Verification</h4>
                <p>Enter your phone number to get started</p>
              </div>
            </div>
            <div className={`step-item ${otpSent ? "active" : ""}`}>
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>OTP Verification</h4>
                <p>Verify the code sent to your phone</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Complete Profile</h4>
                <p>Fill in your details to finish setup</p>
              </div>
            </div>
          </div>
        </div>

        <div className="signup-right">
          <div className="signup-card">
            <div className="card-header">
              <button className="back-button" onClick={() => navigate("/login")}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back to Login
              </button>
              <h2>Create Account</h2>
              <p>Sign up to get started with Altrange</p>
            </div>

            {notification.message && (
              <div className={`notification ${notification.type}`}>
                <div className="notification-content">
                  <span>{notification.message}</span>
                </div>
              </div>
            )}

            <div className="signup-form">
              {!otpSent ? (
                <div className="phone-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <div>
                      <h3>Enter Phone Number</h3>
                      <p>We'll send you a verification code</p>
                    </div>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Phone Number</label>
                    <div className="phone-input-wrapper">
                      <select
                        value={selectedCountry.code}
                        onChange={(e) => {
                          const country = countryCodes.find((c) => c.code === e.target.value)
                          setSelectedCountry(country)
                        }}
                        onKeyDown={(e) => handleKeyDown(e, handleSendOTP)}
                        className="country-select"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code + country.name} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        placeholder={selectedCountry.code === "+91" ? "Enter 10-digit number" : "Enter phone number"}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => handleKeyDown(e, handleSendOTP)}
                        className="phone-input"
                        maxLength="15"
                      />
                    </div>
                  </div>

                  <button className="signup-button primary" onClick={handleSendOTP} disabled={isLoading}>
                    {isLoading ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <>
                        <span>Send Verification Code</span>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                      </>
                    )}
                  </button>

                  <div id="recaptcha-container-signup"></div>
                </div>
              ) : (
                <div className="otp-section">
                  <div className="otp-header">
                    <div className="otp-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    </div>
                    <h3>Verify Your Phone</h3>
                    <p>
                      Enter the 6-digit code sent to
                      <br />
                      <strong>
                        {selectedCountry.code}
                        {phone}
                      </strong>
                    </p>
                  </div>

                  <div className="input-group">
                    <div className="otp-input-wrapper">
                      <input
  type="text"
  placeholder="Enter 6-digit code"
  value={otp}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6)
    setOtp(value)

    if (value.length === 6) {
      // small delay to ensure OTP state updates before verifying
      setTimeout(() => {
        handleVerifyOTP(value)
      }, 300)
    }
  }}
  onKeyDown={(e) => handleKeyDown(e, handleVerifyOTP)}
  className="otp-input"
  maxLength="6"
/>

                    </div>
                    <div className="timer-display">
                      <span className={`timer ${timeoutSeconds <= 10 ? "warning" : ""}`}>
                        Code expires in {Math.floor(timeoutSeconds / 60)}:
                        {(timeoutSeconds % 60).toString().padStart(2, "0")}
                      </span>
                    </div>
                  </div>

                  <button className="signup-button primary" onClick={handleVerifyOTP} disabled={isLoading}>
                    {isLoading ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <>
                        <span>Verify & Continue</span>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      </>
                    )}
                  </button>

                  <div className="otp-actions">
                    <button className="text-button" onClick={resetToInitialState}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" />
                        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                      Change Number
                    </button>
                    <button className="text-button" onClick={handleSendOTP} disabled={isLoading}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23,4 23,10 17,10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                      Resend Code
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignupScreen
